import { useState } from "react";
import { toast } from "sonner";
import { Award, Send, MapPin, Pencil, FileBadge } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, ProgressLine } from "@/components/kit";
import { SealBadge } from "@/components/Seal";
import { DIALOG_CLS } from "@/components/Dialogs";
import { companyScore, SEALS } from "@/lib/quality";
import { PROFILES } from "@/data/mock";
import { fmtDate } from "@/lib/format";

function MySeal({ me }) {
  const { updateCompany } = useStore();
  const [edit, setEdit] = useState(false);
  const [services, setServices] = useState(me.services);
  const status = me.seal ? `${SEALS[me.seal.level].label} aprovado pela consultora` : me.suggested ? `Pontuação para ${SEALS[me.suggested].label} · aguardando aprovação da consultora` : `Faltam ${SEALS.bronze.min - me.score} pts para o Selo Bronze`;
  return (
    <Panel title="Meu selo de qualidade" testId="my-seal" action={<SealBadge level={me.seal?.level} testId="my-seal-badge" />}>
      <p className="font-display text-4xl font-semibold" data-testid="my-score">{me.score}<span className="text-sm text-neutral-400"> pts</span></p>
      <p className="mt-1 text-xs text-neutral-500" data-testid="my-seal-status">{status}</p>
      <div className="mt-4"><ProgressLine value={me.score} /></div>
      {me.seal && <Button asChild size="sm" variant="outline" className="mt-4" data-testid="my-seal-cert"><Link to={`/certificado?tipo=selo&empresa=${me.id}`}><FileBadge className="mr-1 h-4 w-4 text-gold-dark" /> Certificado do selo</Link></Button>}
      <div className="mt-6 border-t border-neutral-100 pt-4">
        <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Serviços para indicação</p>{!edit && <button onClick={() => setEdit(true)} aria-label="Editar serviços" data-testid="my-services-edit" className="text-neutral-400 hover:text-gold-dark"><Pencil className="h-4 w-4" /></button>}</div>
        {edit ? (
          <div className="mt-2 space-y-2">
            <Textarea rows={3} value={services} onChange={(e) => setServices(e.target.value)} data-testid="my-services-input" />
            <Button size="sm" className="font-semibold" onClick={() => { updateCompany(me.id, { services: services.trim() }); setEdit(false); toast.success("Serviços atualizados."); }} data-testid="my-services-save">Salvar</Button>
          </div>
        ) : <p className="mt-2 text-sm text-neutral-700" data-testid="my-services">{me.services || "Descreva seus serviços para receber indicações."}</p>}
      </div>
    </Panel>
  );
}

function ReferDialog({ target, onClose, onSend }) {
  const [note, setNote] = useState("");
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={DIALOG_CLS} data-testid="refer-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Indicar {target?.name}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-neutral-500">Registre a indicação feita para a empresa parceira.</p>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: indiquei uma cliente para festa de aniversário" data-testid="refer-note-input" />
        <Button className="w-full font-semibold" onClick={() => { if (!note.trim()) return toast.error("Descreva a indicação."); onSend(note.trim()); setNote(""); }} data-testid="refer-send-button"><Send className="mr-1 h-4 w-4" /> Registrar indicação</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function OwnerNetwork() {
  const { state, addReferral } = useStore();
  const cid = PROFILES.empresaria.companyId;
  const [target, setTarget] = useState(null);
  const me = companyScore(state, state.companies.find((c) => c.id === cid));
  const partners = state.companies.filter((c) => c.id !== cid && state.seals[c.id]);
  const name = (id) => state.companies.find((c) => c.id === id)?.name;
  const mine = state.referrals.filter((r) => r.fromCompanyId === cid || r.toCompanyId === cid);
  return (
    <div data-testid="owner-network-page">
      <PageHeader eyebrow="Rede Bellas" title="Selo e indicações" subtitle="Empresas com selo de qualidade aprovado pela consultoria, prontas para trocar indicações." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <MySeal me={me} />
        <Panel title="Empresas recomendadas" subtitle="Somente empresas com selo aprovado" className="lg:col-span-2" testId="partner-list">
          {!partners.length && <p className="text-sm text-neutral-400">Nenhuma empresa parceira com selo ainda.</p>}
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {partners.map((c) => (
              <li key={c.id} data-testid={`partner-${c.id}`} className="flex flex-col rounded-xl border border-neutral-200 p-5">
                <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold">{c.name}</p><SealBadge level={state.seals[c.id].level} /></div>
                <p className="mt-1 flex items-center gap-1 text-xs text-neutral-500"><MapPin className="h-3 w-3" /> {c.segment} · {c.city}</p>
                <p className="mt-3 flex-1 text-sm text-neutral-700">{c.services}</p>
                <Button size="sm" variant="outline" className="mt-4 self-start" onClick={() => setTarget(c)} data-testid={`refer-${c.id}`}><Award className="mr-1 h-4 w-4 text-gold-dark" /> Indicar</Button>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Histórico de indicações" className="lg:col-span-3" testId="referral-history">
          {!mine.length && <p className="text-sm text-neutral-400">Nenhuma indicação registrada.</p>}
          <ul className="divide-y divide-neutral-100">
            {mine.map((r) => (
              <li key={r.id} data-testid={`referral-${r.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 text-sm">
                <span className="font-medium">{r.fromCompanyId === cid ? `Enviada para ${name(r.toCompanyId)}` : `Recebida de ${name(r.fromCompanyId)}`}</span>
                <span className="flex-1 text-neutral-500">{r.note}</span>
                <span className="text-xs text-neutral-400">{fmtDate(r.date)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
      <ReferDialog target={target} onClose={() => setTarget(null)} onSend={(note) => { addReferral({ fromCompanyId: cid, toCompanyId: target.id, note }); toast.success(`Indicação para ${target.name} registrada.`); setTarget(null); }} />
    </div>
  );
}
